import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import ChatRoomTable from '../components/ChatRoomTable';
import { useDeleteChatRoom } from '../hooks/useChatMutations';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { userApi } from '@/features/users/api/user.api';
import { roomAdApi } from '@/features/room-ads/api/roomAd.api';
import { roomWantedApi } from '@/features/room-wanted/api/roomWanted.api';

export default function ChatRoomListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const list = useDynamicAdminList<any>(ADMIN_FILTER_RESOURCES.chatRooms, {
    initialSortField: 'id',
    initialSortDir: 'desc',
  });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteChatRoom();

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'owner.id': {
      fetchFn: ({ search, page, size }) => userApi.list({ page, size, firstName: search }),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `#${u.id}` }),
      placeholder: t('filters.ownerUserId'),
    },
    'starter.id': {
      fetchFn: ({ search, page, size }) => userApi.list({ page, size, firstName: search }),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `#${u.id}` }),
      placeholder: t('filters.starterUserId'),
    },
    'roomAd.id': {
      fetchFn: ({ search, page, size }) => roomAdApi.list({ page, size, title: search }),
      mapOption: (r: any) => ({ value: r.id, label: r.title ? `#${r.id} ${r.title}` : `#${r.id}` }),
      placeholder: t('filters.roomAdId'),
    },
    'roomWantedAd.id': {
      fetchFn: ({ search, page, size }) => roomWantedApi.list({ page, size, title: search }),
      mapOption: (r: any) => ({ value: r.id, label: r.title ? `#${r.id} ${r.title}` : `#${r.id}` }),
      placeholder: t('filters.roomWantedAdId'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader title={t('chat.rooms')} subtitle={t('chat.roomsSubtitle')} />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data} value={list.filters} onChange={list.setFilters} onReset={list.resetFilters} relationLookups={relationLookups} />
      <div style={{ marginTop: 16 }}>
        <ChatRoomTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onView={(id: number) => navigate(`/chat/messages?chatRoomId=${id}`)}
          onDelete={(item: any) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog open={!!deleteTarget} title={t('chat.deleteRoom')}
        message={t('chat.deleteRoomMessage', { id: deleteTarget?.id })}
        onConfirm={handleConfirmDelete} onCancel={() => setDeleteTarget(null)} loading={deleteMutation.isPending} />
    </div>
  );
}
